import React, { useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { allThemes } from "../../modules/themes";
import { ThemeModule } from "../../modules/themes/theme.types";
import {
  Module,
  MicroCourse,
  Quiz,
  Resource,
  allModules,
  allMicroCourses,
  allQuizzes,
  allResources,
} from "../../modules/academy/models";
import { ThemeHeader } from "./components/ThemeHeader";
import { ThemeOverview } from "./components/ThemeOverview";
import { ModuleList } from "./components/ModuleList";
import { MicroCourseList } from "./components/MicroCourseList";
import { QuizList } from "./components/QuizList";
import { ResourceList } from "./components/ResourceList";
import { ThemeFooter } from "./components/ThemeFooter";
import { useProgression } from "../../context/ProgressionContext";
import { useIsKidProfile, useIsTeacherProfile } from "../../hooks/useProfileRole";

export default function ThemePage() {
  const { themeId } = useParams<{ themeId: string }>();
  const { accessProfile } = useAuth();
  const { progress, isGuest, markMicroCourseCompleted, setInProgressMicroCourse, setQuizScore } =
    useProgression();
  const isKidProfile = useIsKidProfile();
  const isTeacherProfile = useIsTeacherProfile();

  const theme: ThemeModule | undefined = useMemo(
    () => allThemes.find((t) => t.id === themeId),
    [themeId]
  );

  if (!theme) {
    return (
      <main className="max-w-3xl mx-auto py-10 space-y-4">
        <h1 className="text-2xl font-bold">Theme introuvable</h1>
        <p className="text-sm text-slate-600 dark:text-slate-300">
          Le theme demande n'existe pas ou plus. Retournez vers Academy ou choisissez un autre theme.
        </p>
        <Link to="/academy" className="text-blue-600 underline">
          Retour vers MixMind Academy
        </Link>
      </main>
    );
  }

  const estimatedDurationLabel = theme.estimatedDuration ? `${theme.estimatedDuration} min` : undefined;
  const themeProgressPercent = progress?.themeProgress[theme.id] ?? 0;

  const themeModules: Module[] = useMemo(() => {
    if (!theme.moduleIds) return [];
    return theme.moduleIds
      .map((id) => allModules.find((m) => m.id === id))
      .filter(Boolean) as Module[];
  }, [theme.moduleIds]);

  const themeMicroCourses: MicroCourse[] = useMemo(() => {
    if (!theme.microCourseIds) return [];
    return theme.microCourseIds
      .map((id) => allMicroCourses.find((m) => m.id === id))
      .filter(Boolean) as MicroCourse[];
  }, [theme.microCourseIds]);

  const themeQuizzes: Quiz[] = useMemo(() => {
    if (!theme.quizIds) return [];
    return theme.quizIds
      .map((id) => allQuizzes.find((q) => q.id === id))
      .filter(Boolean) as Quiz[];
  }, [theme.quizIds]);

  const themeResources: Resource[] = useMemo(() => {
    if (!theme.resourceIds) return [];
    return theme.resourceIds
      .map((id) => allResources.find((r) => r.id === id))
      .filter(Boolean) as Resource[];
  }, [theme.resourceIds]);

  return (
    <main className="max-w-5xl mx-auto py-8 px-4 space-y-6">
      <ThemeHeader
        title={theme.title}
        level={
          theme.level === "debutant"
            ? "beginner"
            : theme.level === "intermediaire"
            ? "intermediate"
            : theme.level === "avance"
            ? "advanced"
            : undefined
        }
        audience={theme.audience}
        estimatedDurationLabel={isKidProfile ? undefined : estimatedDurationLabel}
        progressionPercent={isKidProfile ? 0 : themeProgressPercent}
      />

      <ThemeOverview
        longDescription={theme.longDescription || theme.description}
        objectives={theme.objectives}
        audience={theme.audience}
      />

      <ModuleList
        modules={themeModules}
        completedMicroCourses={progress?.completedMicroCourses}
        isKid={isKidProfile}
        onOpenModule={(id) => console.log("open module", id)}
      />
      <MicroCourseList
        microCourses={themeMicroCourses}
        completedMicroCourses={progress?.completedMicroCourses}
        inProgressMicroCourseId={progress?.inProgressMicroCourseId ?? null}
        isGuest={isGuest}
        isKid={isKidProfile}
        onStartMicroCourse={(id) => {
          if (!isGuest) setInProgressMicroCourse(id);
        }}
        onMarkCompleted={(id) => markMicroCourseCompleted(id, theme.id, theme.moduleIds)}
      />
      <QuizList
        quizzes={themeQuizzes}
        quizScores={progress?.quizScores}
        isGuest={isGuest}
        onStartQuiz={(id) => console.log("start quiz", id)}
      />
      <ResourceList resources={themeResources} />
      <ThemeFooter theme={theme} relatedThemes={[]} relatedParcoursIds={["parcours-biais-1"]} />

      {/* TODO MixMind: utiliser accessProfile pour adapter certains blocs (ex: audience) */}
      {isGuest && (
        <p className="text-[11px] text-slate-500 dark:text-slate-400">
          Mode invite : connectez-vous pour suivre votre progression.
        </p>
      )}
    </main>
  );
}
